import { ContactMail, ContactPhone, LocationOn, PermContactCalendar } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { EditContactDialog } from "components/job/details/EditContactDialog";
import { chooseContact } from "helpers";
import { addressToGoogleString, addressToString } from "helpers/addressToString";
import { AddressType, CustomerContactType, Maybe } from "operations/schema/schema";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { setEditContactOpen } from "store/slices/jobs.store";
import { isProductEvatic } from "store/slices/user.store";

interface ContactsProps {
  customerAddress: AddressType;
  contacts: Maybe<CustomerContactType>[];
  preferredContact?: Maybe<CustomerContactType>;
  includeCountry?: Boolean;
  readOnly?: Boolean;
}

const Contacts: FC<ContactsProps> = (props) => {
  const { contacts, preferredContact, customerAddress, includeCountry, readOnly } = props;
  const firstContact = chooseContact(preferredContact, contacts);
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isEvatic = useAppSelector(isProductEvatic);
  const { page } = useAppSelector((s) => s.root);
  const editContactEnabled = !readOnly && isEvatic && page === "jobs";

  const addressString = addressToString(customerAddress, includeCountry);
  const googleString = addressToGoogleString(customerAddress, includeCountry);

  const address = (
    <ListItem
      component="a"
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(googleString)}`}
      disableGutters
      target="_blank"
      rel="noopener noreferrer"
    >
      <ListItemIcon className="min-width-38">
        <LocationOn />
      </ListItemIcon>
      <ListItemText primary={addressString} />
    </ListItem>
  );

  const firstContactDetails = (address: JSX.Element, firstContact: CustomerContactType) => {
    return (
      <>
        {firstContact?.name && (
          <ListItem disableGutters>
            <ListItemIcon className="min-width-38">
              <PermContactCalendar />
            </ListItemIcon>
            <ListItemText primary={firstContact.name} />
          </ListItem>
        )}
        {address}
        {firstContact?.phoneNumber && (
          <ListItem component="a" href={`tel:${firstContact.phoneNumber}`} disableGutters>
            <ListItemIcon className="min-width-38">
              <ContactPhone />
            </ListItemIcon>
            <ListItemText primary={firstContact.phoneNumber} />
          </ListItem>
        )}
        {firstContact?.mobileNumber && (
          <ListItem component="a" href={`tel:${firstContact.mobileNumber}`} disableGutters>
            <ListItemIcon className="min-width-38">
              <ContactPhone />
            </ListItemIcon>
            <ListItemText primary={firstContact.mobileNumber} />
          </ListItem>
        )}
        {firstContact?.email && (
          <ListItem component="a" href={`mailto:${firstContact.email}`} disableGutters>
            <ListItemIcon className="min-width-38">
              <ContactMail />
            </ListItemIcon>
            <ListItemText primary={firstContact.email} />
          </ListItem>
        )}
      </>
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.2rem",
          }}
        >
          <FormattedMessage id="contact" />
        </Typography>
        {editContactEnabled && (
          <IconButton
            aria-label={intl.formatMessage({ id: "visit.addEditContact" })}
            onClick={() =>
              dispatch(
                setEditContactOpen({
                  open: true,
                })
              )
            }
            data-testid="Contacts-EditButton"
            sx={{
              fontSize: "1.2rem",
              paddingY: 0,
            }}
          >
            <EditIcon color="primary" />
          </IconButton>
        )}
      </div>
      <List dense>
        {firstContact ? (
          firstContactDetails(address, firstContact)
        ) : (
          <>
            {address}
            <br />
            <Typography>{intl.formatMessage({ id: "contact.noCustomerContacts" })}</Typography>
          </>
        )}
      </List>
      {editContactEnabled && <EditContactDialog />}
    </>
  );
};

export default Contacts;
